/**
 * AbaVivencias — Vivências Terapêuticas
 *
 * Hub de séries terapêuticas. A série atual é a Jornada das 12 Emoções,
 * vendida como produto independente (não incluída nos planos).
 * A administradora cadastra clientes com acesso gratuito.
 * A consultora usa créditos (R$30) para liberar 1 cliente por crédito.
 */
import { useState, useEffect } from 'react'
import { lumi } from '@/lib/lumi'
import toast from 'react-hot-toast'
import {Sparkles, Plus, Copy, Trash2, X, AlertTriangle, User as UserIcon, ArrowRight, ArrowLeft, Shield} from 'lucide-react'
import { copiarTexto } from '@/utils/clipboard'

type TelaVivencia = 'hub' | 'saiba-mais' | 'gestao'

const BASE_URL = 'https://www.descubraseusoleos.com.br'
const LINK_JORNADA_AVULSA = 'https://pay.hotmart.com/T107316970V?off=pe7x8qb8'
const LINK_JORNADA_CREDITO = 'https://pay.hotmart.com/T107316970V?off=oqxf77xx'
const EMAIL_FUNCTION_URL = 'https://api.lumi.new/v1/functions/p427216140118241280/send-welcome-email'

const COMO_FUNCIONA = [
  'Você avança no seu ritmo — pode fazer mais de uma vivência no mesmo dia',
  'Cada vivência usa um óleo essencial diferente',
  'Você escreve suas respostas diretamente online pelo celular ou computador',
  'Você responde perguntas terapêuticas guiadas',
  'Recebe um relatório personalizado no seu e-mail',
  'Assim que você conclui uma vivência, a próxima fica disponível imediatamente',
  'Você tem 30 dias para concluir todas as vivências',
]

interface VivenciaCliente {
  _id: string
  nome: string
  whatsapp?: string
  email?: string
  consultora: string
  token: string
  vivenciaAtual: number
  vivenciasConcluidas?: number[]
  status: 'ativo' | 'concluido' | 'expirado' | 'inativo'
  origem?: string
  dataInicio: string
  dataExpiracao?: string
  createdAt: string
}

interface Props {
  consultoraAdmin: string
  isAdmin?: boolean
  podeAcessar?: boolean
}

function gerarToken(): string {
  return Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 6)
}

function slugIgual(a?: string, b?: string): boolean {
  return String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase()
}

function lerSessaoPainel(): { tipo: 'admin' | 'consultora' | ''; slug: string } {
  try {
    const raw = localStorage.getItem('admin2_sessao')
    if (!raw) return { tipo: '', slug: '' }
    const parsed = JSON.parse(raw) as { tipo?: string; usuario?: string }
    const tipo = String(parsed?.tipo || '').trim()
    const slug = String(parsed?.usuario || '').trim()
    if (tipo === 'consultora') return { tipo: 'consultora', slug }
    if (tipo === 'admin') return { tipo: 'admin', slug: 'monica' }
  } catch {
    /* sessao invalida */
  }
  return { tipo: '', slug: '' }
}

function origemDoCliente(cliente: VivenciaCliente): string {
  return String(cliente?.origem || '').trim().toLowerCase()
}

function ehJornadaGratuitaPropria(cliente: VivenciaCliente): boolean {
  return origemDoCliente(cliente) === 'consultora_gratuita'
}

function clienteDaConsultora(cliente: VivenciaCliente, slug: string): boolean {
  if (!slug) return false
  if (!slugIgual(cliente?.consultora, slug)) return false
  const origem = origemDoCliente(cliente)
  if (origem === 'admin' || origem === 'consultora_gratuita') return false
  return true
}

function jornadaJaUtilizada(cliente: VivenciaCliente | null): boolean {
  if (!cliente) return false
  if (cliente.status === 'concluido' || cliente.status === 'expirado') return true
  const atual = Number(cliente.vivenciaAtual) || 1
  const concluidas = Array.isArray(cliente.vivenciasConcluidas) ? cliente.vivenciasConcluidas : []
  return atual > 1 || concluidas.length > 0
}

export default function AbaVivencias({ consultoraAdmin, isAdmin = false }: Props) {
  const sessao = lerSessaoPainel()
  // Consultora logada NUNCA entra em visao admin, mesmo se a prop isAdmin vier errada.
  const visaoAdmin = sessao.tipo !== 'consultora' && (sessao.tipo === 'admin' || isAdmin === true)
  const slugLogado = sessao.tipo === 'consultora'
    ? sessao.slug
    : visaoAdmin
      ? String(consultoraAdmin || sessao.slug || 'monica').trim()
      : String(consultoraAdmin || sessao.slug || '').trim()
  const soConsultora = sessao.tipo === 'consultora' || !visaoAdmin

  const [clientes, setClientes] = useState<VivenciaCliente[]>([])
  const [carregando, setCarregando] = useState(true)
  const [modalAberto, setModalAberto] = useState(false)
  const [salvando, setSalvando] = useState(false)
  const [confirmarDelete, setConfirmarDelete] = useState<VivenciaCliente | null>(null)
  const [deletando, setDeletando] = useState(false)

  const [tela, setTela] = useState<TelaVivencia>('hub')
  const [nome, setNome] = useState('')
  const [whatsapp, setWhatsapp] = useState('')
  const [email, setEmail] = useState('')

  const [consultoraId, setConsultoraId] = useState<string | null>(null)
  const [creditos, setCreditos] = useState(0)
  const [carregandoCreditos, setCarregandoCreditos] = useState(soConsultora)
  const [jornadaGratuita, setJornadaGratuita] = useState<VivenciaCliente | null>(null)
  const [gerandoGratuita, setGerandoGratuita] = useState(false)

  useEffect(() => {
    void carregarClientes()
    if (soConsultora && slugLogado) {
      void carregarCreditos()
      void garantirJornadaGratuita()
    } else {
      setJornadaGratuita(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [soConsultora, slugLogado])

  async function buscarConsultoraLogada() {
    if (!slugLogado) return null
    const res = await lumi.entities.consultoras.list({
      filter: { usuario: slugLogado },
      limit: 50,
    })
    const lista = res?.list ?? []
    return lista.find((c: { usuario?: string }) => slugIgual(c?.usuario, slugLogado)) ?? null
  }

  async function carregarCreditos() {
    if (visaoAdmin || !slugLogado) {
      setCarregandoCreditos(false)
      setConsultoraId(null)
      setCreditos(0)
      return
    }
    setCarregandoCreditos(true)
    try {
      const atual = await buscarConsultoraLogada()
      if (!atual?._id || !slugIgual(atual?.usuario, slugLogado)) {
        setConsultoraId(null)
        setCreditos(0)
        return
      }
      setConsultoraId(atual._id)
      setCreditos(Number(atual?.creditosJornada) || 0)
    } catch (e) {
      console.error('[vivencias] erro ao carregar créditos', e)
      setConsultoraId(null)
      setCreditos(0)
    } finally {
      setCarregandoCreditos(false)
    }
  }

  async function carregarClientes() {
    setCarregando(true)
    try {
      if (!visaoAdmin && !slugLogado) {
        setClientes([])
        return
      }
      const filtro = visaoAdmin ? {} : { consultora: slugLogado }
      const res = await lumi.entities.vivencias_clientes.list({ filter: filtro, sort: { createdAt: -1 }, limit: 300 })
      const lista = (res?.list ?? []) as VivenciaCliente[]
      const isolados = visaoAdmin
        ? lista
        : lista.filter((c) => clienteDaConsultora(c, slugLogado))
      setClientes(isolados)
      if (soConsultora && slugLogado) {
        const propria = lista.find((c) => ehJornadaGratuitaPropria(c) && slugIgual(c?.consultora, slugLogado))
        if (propria) setJornadaGratuita(propria)
      }
    } catch (e) {
      console.error('[vivencias] erro ao carregar clientes', e)
      setClientes([])
    } finally {
      setCarregando(false)
    }
  }

  async function garantirJornadaGratuita() {
    if (visaoAdmin || !slugLogado) {
      setJornadaGratuita(null)
      setGerandoGratuita(false)
      return
    }
    setGerandoGratuita(true)
    try {
      const consultora = await buscarConsultoraLogada()
      if (!consultora?._id || !slugIgual(consultora?.usuario, slugLogado)) {
        setJornadaGratuita(null)
        return
      }
      const res = await lumi.entities.vivencias_clientes.list({
        filter: { consultora: slugLogado },
        sort: { createdAt: -1 },
        limit: 300,
      })
      const lista = (res?.list ?? []) as VivenciaCliente[]
      const emailConsultora = String(consultora?.email || '').trim().toLowerCase()
      const existente = lista.find((c) => {
        if (!slugIgual(c?.consultora, slugLogado)) return false
        if (ehJornadaGratuitaPropria(c)) return true
        if (emailConsultora && String(c?.email || '').trim().toLowerCase() === emailConsultora && origemDoCliente(c) !== 'admin') {
          return true
        }
        return false
      }) ?? null
      if (existente) {
        if (!ehJornadaGratuitaPropria(existente) && existente._id) {
          try {
            await lumi.entities.vivencias_clientes.update(existente._id, {
              origem: 'consultora_gratuita',
              updatedAt: new Date().toISOString(),
            })
            existente.origem = 'consultora_gratuita'
          } catch (marcacaoErr) {
            console.error('[vivencias] erro ao marcar jornada gratuita', marcacaoErr)
          }
        }
        setJornadaGratuita(existente)
        return
      }
      const nomeTrim = String(consultora?.nome || slugLogado).trim()
      if (!emailConsultora) {
        setJornadaGratuita(null)
        return
      }
      const agora = new Date().toISOString()
      const token = gerarToken()
      const criado = await lumi.entities.vivencias_clientes.create({
        nome: nomeTrim,
        whatsapp: String(consultora?.whatsapp || '').trim(),
        email: emailConsultora,
        consultora: slugLogado,
        token,
        vivenciaAtual: 1,
        vivenciasConcluidas: [],
        status: 'ativo',
        origem: 'consultora_gratuita',
        dataInicio: agora,
        dataLiberacaoAtual: agora,
        dataExpiracao: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        enviouAvisoExpiracao: false,
        enviouLembreteVivencia: false,
        lembreteVivenciaNumero: 0,
        enviouLembreteReengajamento24h: false,
        creator: slugLogado,
        createdAt: agora,
        updatedAt: agora,
      }) as VivenciaCliente
      setJornadaGratuita(criado)
    } catch (e) {
      console.error('[vivencias] erro ao gerar jornada gratuita', e)
    } finally {
      setGerandoGratuita(false)
    }
  }

  function abrirJornadaGratuita() {
    if (!jornadaGratuita?.token) {
      toast.error('Seu acesso gratuito ainda não está pronto. Atualize a página.')
      return
    }
    window.open(`${BASE_URL}/vivencias/${jornadaGratuita.token}`, '_blank', 'noopener,noreferrer')
  }

  const handleSalvar = async (e: React.FormEvent) => {
    e.preventDefault()
    const nomeTrim = nome.trim()
    const whatsappTrim = whatsapp.trim()
    const emailTrim = email.trim().toLowerCase()
    if (!nomeTrim) {
      toast.error('O nome é obrigatório')
      return
    }
    if (!emailTrim) {
      toast.error('O e-mail é obrigatório para envio do link de acesso e dos relatórios das vivências.')
      return
    }
    if (!visaoAdmin && !slugLogado) {
      toast.error('Sessão da consultora inválida. Entre novamente.')
      return
    }
    if (!visaoAdmin && creditos < 1) {
      toast.error('Você não tem créditos disponíveis. Compre 1 crédito para liberar esta cliente.')
      return
    }
    setSalvando(true)
    try {
      if (!visaoAdmin) {
        const atual = await buscarConsultoraLogada()
        const saldoAtual = Number(atual?.creditosJornada) || 0
        if (!atual?._id || !slugIgual(atual?.usuario, slugLogado) || saldoAtual < 1) {
          toast.error('Você não tem créditos disponíveis.')
          setCreditos(saldoAtual)
          setConsultoraId(atual?._id && slugIgual(atual?.usuario, slugLogado) ? atual._id : null)
          setSalvando(false)
          return
        }
        await lumi.entities.consultoras.update(atual._id, {
          creditosJornada: saldoAtual - 1,
          updatedAt: new Date().toISOString(),
        })
        setConsultoraId(atual._id)
        setCreditos(saldoAtual - 1)
      }

      const now = new Date().toISOString()
      const token = gerarToken()
      await lumi.entities.vivencias_clientes.create({
        nome: nomeTrim,
        whatsapp: whatsappTrim,
        email: emailTrim,
        consultora: slugLogado,
        token,
        vivenciaAtual: 1,
        vivenciasConcluidas: [],
        status: 'ativo',
        origem: visaoAdmin ? 'admin' : 'consultora',
        dataInicio: now,
        dataLiberacaoAtual: now,
        dataExpiracao: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        enviouAvisoExpiracao: false,
        enviouLembreteVivencia: false,
        lembreteVivenciaNumero: 0,
        enviouLembreteReengajamento24h: false,
        creator: slugLogado,
        createdAt: now,
        updatedAt: now,
      })

      const link = `${BASE_URL}/vivencias/${token}`
      try {
        await fetch(EMAIL_FUNCTION_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tipo: 'vivencia_acesso',
            nome: nomeTrim,
            email: emailTrim,
            linkAcesso: link,
          }),
        })
      } catch (emailErr) {
        console.error('[vivencias] erro ao enviar e-mail de acesso', emailErr)
      }

      toast.success(`Cliente "${nomeTrim}" cadastrada! Vivência 1 liberada.`)
      setNome('')
      setWhatsapp('')
      setEmail('')
      setModalAberto(false)
      await carregarClientes()
    } catch (err) {
      console.error('[vivencias] erro ao cadastrar cliente', err)
      toast.error('Erro ao cadastrar cliente')
    } finally {
      setSalvando(false)
    }
  }

  const copiarLink = async (cliente: VivenciaCliente) => {
    if (!visaoAdmin && !slugIgual(cliente?.consultora, slugLogado)) return
    const link = `${BASE_URL}/vivencias/${cliente.token}`
    try {
      await copiarTexto(link)
      toast.success('Link copiado!')
    } catch {
      toast.error('Erro ao copiar link')
    }
  }

  const deletarCliente = async () => {
    if (!confirmarDelete) return
    if (!visaoAdmin && !slugIgual(confirmarDelete?.consultora, slugLogado)) {
      toast.error('Você só pode remover clientes cadastradas com os seus créditos.')
      setConfirmarDelete(null)
      return
    }
    setDeletando(true)
    try {
      await lumi.entities.vivencias_clientes.delete(confirmarDelete._id)
      toast.success(`"${confirmarDelete.nome}" removida das Vivências.`)
      setConfirmarDelete(null)
      await carregarClientes()
    } catch (err) {
      console.error('[vivencias] erro ao deletar cliente', err)
      toast.error('Erro ao excluir cliente')
    } finally {
      setDeletando(false)
    }
  }

  const corStatus = (status: string): { bg: string; text: string } => {
    switch (status) {
      case 'ativo': return { bg: '#D8F3DC', text: '#2D6A4F' }
      case 'concluido': return { bg: '#F3E8FF', text: '#6B21A8' }
      case 'expirado': return { bg: '#FCEBEB', text: '#A32D2D' }
      default: return { bg: '#F3F4F6', text: '#4B5563' }
    }
  }

  const labelStatus = (status: string): string => {
    const map: Record<string, string> = { ativo: 'Em andamento', concluido: 'Concluído', expirado: 'Expirado', inativo: 'Inativo' }
    return map[status] || status
  }

  const labelProgresso = (cliente: VivenciaCliente): string => {
    if (cliente.status === 'concluido') return 'Jornada concluída'
    const atual = Number(cliente.vivenciaAtual) || 1
    const concluidas = Array.isArray(cliente.vivenciasConcluidas) ? cliente.vivenciasConcluidas : []
    if (concluidas.includes(atual)) {
      return `Vivência ${atual} — Respondida`
    }
    return `Vivência ${atual} — Aguardando resposta`
  }

  if (tela === 'hub') {
    return (
      <>
        <div className="mb-8">
          <h2 className="text-2xl font-cormorant font-semibold text-texto-escuro flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-verde-escuro" />
            Vivências Terapêuticas
          </h2>
          <p className="text-sm text-texto-suave mt-1">
            Uma jornada guiada com os óleos essenciais do Kit Brasil dōTERRA.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="bg-white rounded-2xl shadow-md border border-[#EDE0D4] p-6 flex flex-col">
            <p className="text-xs uppercase tracking-wide text-[#B08968] font-semibold mb-2">Série</p>
            <h3 className="text-xl font-cormorant font-semibold text-[#2D6A4F] mb-3">
              Jornada das 12 Emoções
            </h3>
            <p className="text-sm text-[#5F5E5A] leading-relaxed mb-6 flex-1">
              Uma experiência terapêutica guiada de 12 dias com os óleos essenciais do Kit Brasil dōTERRA.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => setTela('saiba-mais')}
                className="inline-flex items-center justify-center gap-2 bg-[#2D6A4F] text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-opacity-90 transition-colors"
              >
                Saiba mais
                <ArrowRight className="w-4 h-4" />
              </button>
              {visaoAdmin ? (
                <button
                  type="button"
                  onClick={() => setTela('gestao')}
                  className="inline-flex items-center justify-center gap-2 border-2 border-[#2D6A4F] text-[#2D6A4F] px-5 py-2.5 rounded-full text-sm font-medium hover:bg-[#D8F3DC] transition-colors"
                >
                  Acesso gratuito
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setTela('gestao')}
                  className="inline-flex items-center justify-center gap-2 border-2 border-[#2D6A4F] text-[#2D6A4F] px-5 py-2.5 rounded-full text-sm font-medium hover:bg-[#D8F3DC] transition-colors"
                >
                  Meus créditos
                </button>
              )}
            </div>
          </div>
        </div>
      </>
    )
  }

  if (tela === 'saiba-mais') {
    return (
      <div className="max-w-3xl">
        <button
          type="button"
          onClick={() => setTela('hub')}
          className="inline-flex items-center gap-2 text-sm text-[#2D6A4F] hover:underline mb-6 font-medium"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar para Vivências Terapêuticas
        </button>

        <div className="mb-8">
          <p className="text-xs uppercase tracking-wide text-[#B08968] font-semibold mb-2">Série</p>
          <h2 className="text-3xl font-cormorant font-semibold text-[#2D6A4F] mb-3">
            Jornada das 12 Emoções
          </h2>
          <p className="text-base text-[#5F5E5A] leading-relaxed">
            Uma experiência terapêutica guiada de 12 dias com os óleos essenciais do Kit Brasil dōTERRA.
          </p>
        </div>

        <section className="bg-white rounded-2xl shadow-md border border-[#EDE0D4] p-6 mb-5">
          <h3 className="text-lg font-cormorant font-semibold text-[#2D6A4F] mb-3">O que é</h3>
          <div className="space-y-4 text-sm text-[#2C2C2A] leading-relaxed">
            <p>
              A Jornada das 12 Emoções é uma vivência transformadora que convida você, através dos óleos essenciais, a acessar bloqueios internos e transformá-los em alívio, libertação e expansão interior.
            </p>
            <p>Uma jornada que promove elevação de vibração e expansão da consciência.</p>
            <p>
              12 vivências terapêuticas guiadas com os óleos essenciais projetadas para promover a liberação de bloqueios emocionais, ressignificação de crenças limitantes e elevação do estado interior. Cada vivência combina o poder terapêutico dos óleos essenciais com uma metodologia de autoconhecimento e transformação, criando uma experiência única de cura, expansão e equilíbrio interior.
            </p>
          </div>
        </section>

        <section className="bg-white rounded-2xl shadow-md border border-[#EDE0D4] p-6 mb-5">
          <h3 className="text-lg font-cormorant font-semibold text-[#2D6A4F] mb-4">Como funciona</h3>
          <ul className="space-y-3">
            {COMO_FUNCIONA.map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm text-[#2C2C2A] leading-relaxed">
                <span className="text-[#2D6A4F] font-semibold mt-0.5">→</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="bg-white rounded-2xl shadow-md border border-[#EDE0D4] p-6 mb-5">
          <h3 className="text-lg font-cormorant font-semibold text-[#2D6A4F] mb-3 flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Sua segurança e privacidade
          </h3>
          <div className="space-y-4 text-sm text-[#2C2C2A] leading-relaxed">
            <p>
              Esta é uma jornada segura e completamente confidencial. Ao responder cada vivência você acessa memórias, emoções e padrões profundos com total liberdade e privacidade.
            </p>
            <p>
              Suas respostas são processadas apenas para gerar seu relatório personalizado e não ficam armazenadas em nenhum lugar. Ninguém terá acesso ao que você escrever. Nem a consultora, nem a administração do sistema.
            </p>
            <p>
              Apenas você receberá o relatório diretamente no seu e-mail. Você pode se aprofundar com total honestidade sabendo que o que você escrever é exclusivamente seu.
            </p>
          </div>
        </section>

        <section className="bg-white rounded-2xl shadow-md border border-[#EDE0D4] p-6 mb-5">
          <h3 className="text-lg font-cormorant font-semibold text-[#2D6A4F] mb-3">Como receber o acesso</h3>
          <p className="text-sm text-[#2C2C2A] leading-relaxed">
            {soConsultora
              ? 'Como assinante do sistema, você tem 1 jornada gratuita gerada automaticamente com o nome e o e-mail do seu perfil. O processo é totalmente online e pode ser feito pelo celular ou computador.'
              : 'Após a confirmação do pagamento você receberá no seu e-mail o link de acesso à sua primeira vivência. O processo é totalmente online e pode ser feito pelo celular ou computador.'}
          </p>
        </section>

        <section className="bg-[#F8FBF9] rounded-2xl shadow-md border-2 border-[#2D6A4F] p-6 text-center">
          {soConsultora ? (
            jornadaJaUtilizada(jornadaGratuita) ? (
              <>
                <p className="text-xs uppercase tracking-wide text-[#B08968] font-semibold mb-2">Sua jornada</p>
                <p className="text-2xl font-cormorant font-semibold text-[#2C2C2A] mb-3">Já utilizada</p>
                <p className="text-sm text-[#5F5E5A] leading-relaxed mb-5">
                  Seu acesso gratuito de assinante já foi iniciado. Você ainda pode retomar pelo link salvo.
                </p>
                {jornadaGratuita?.token ? (
                  <button
                    type="button"
                    onClick={abrirJornadaGratuita}
                    className="inline-flex items-center justify-center gap-2 bg-[#2D6A4F] text-white px-8 py-3 rounded-full text-sm font-semibold hover:bg-opacity-90 transition-colors"
                  >
                    Continuar minha jornada
                    <ArrowRight className="w-4 h-4" />
                  </button>
                ) : null}
              </>
            ) : (
              <>
                <p className="text-xs uppercase tracking-wide text-[#B08968] font-semibold mb-2">Benefício de assinante</p>
                <p className="text-2xl font-cormorant font-semibold text-[#2C2C2A] mb-3">Gratuita para você</p>
                <p className="text-sm text-[#5F5E5A] leading-relaxed mb-5">
                  Você está participando da jornada de forma gratuita por ser assinante do sistema. Sem cobrança e sem formulário.
                </p>
                <button
                  type="button"
                  onClick={abrirJornadaGratuita}
                  disabled={!jornadaGratuita?.token}
                  className="inline-flex items-center justify-center gap-2 bg-[#2D6A4F] text-white px-8 py-3 rounded-full text-sm font-semibold hover:bg-opacity-90 transition-colors disabled:opacity-60"
                >
                  {jornadaGratuita?.token ? 'Fazer minha jornada gratuita' : 'Preparando seu acesso...'}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </>
            )
          ) : (
            <>
              <p className="text-xs uppercase tracking-wide text-[#B08968] font-semibold mb-2">Valor</p>
              <p className="text-2xl font-cormorant font-semibold text-[#2C2C2A] mb-5">
                <span className="line-through text-[#5F5E5A] text-lg mr-2">R$ 497,00</span>
                por R$ 97,00
              </p>
              <a
                href={LINK_JORNADA_AVULSA}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#2D6A4F] text-white px-8 py-3 rounded-full text-sm font-semibold hover:bg-opacity-90 transition-colors"
              >
                Quero participar
                <ArrowRight className="w-4 h-4" />
              </a>
            </>
          )}
        </section>
      </div>
    )
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setTela('hub')}
        className="inline-flex items-center gap-2 text-sm text-[#2D6A4F] hover:underline mb-5 font-medium"
      >
        <ArrowLeft className="w-4 h-4" />
        Voltar para Vivências Terapêuticas
      </button>

      {visaoAdmin ? (
        <div className="bg-[#D8F3DC] border border-[#52B788] rounded-2xl p-6 mb-6">
          <h2 className="text-xl font-cormorant font-semibold text-[#2D6A4F] mb-3 flex items-center gap-2">
            <Sparkles className="w-5 h-5" />
            Jornada das 12 Emoções — acesso gratuito
          </h2>
          <p className="text-sm text-[#2C2C2A] leading-relaxed">
            Cadastre manualmente quem deve receber a Jornada sem passar pelo pagamento. Informe nome e e-mail. A Vivência 1 é liberada na hora e o link pode ser enviado por e-mail.
          </p>
        </div>
      ) : (
        <>
          <div className="bg-[#F8FBF9] border-2 border-[#2D6A4F] rounded-2xl p-6 mb-6">
            <p className="text-xs uppercase tracking-wide text-[#B08968] font-semibold mb-2">Benefício de assinante</p>
            <h2 className="text-xl font-cormorant font-semibold text-[#2D6A4F] mb-2">
              Sua vivência gratuita: {jornadaJaUtilizada(jornadaGratuita) ? 'já utilizada' : 'Disponível'}
            </h2>
            {jornadaJaUtilizada(jornadaGratuita) ? (
              <p className="text-sm text-[#5F5E5A] leading-relaxed mb-4">
                Seu acesso gratuito de assinante já foi iniciado. Você ainda pode retomar pelo link gerado automaticamente.
              </p>
            ) : (
              <p className="text-sm text-[#5F5E5A] leading-relaxed mb-4">
                Você está participando da jornada de forma gratuita por ser assinante do sistema. O link foi gerado automaticamente com o nome e o e-mail do seu perfil — sem preencher o formulário de Nova cliente.
              </p>
            )}
            {jornadaGratuita?.token ? (
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <button
                  type="button"
                  onClick={abrirJornadaGratuita}
                  className="inline-flex items-center justify-center gap-2 bg-[#2D6A4F] text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-opacity-90 transition-colors"
                >
                  {jornadaJaUtilizada(jornadaGratuita) ? 'Continuar minha jornada' : 'Começar minha jornada gratuita'}
                  <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => copiarLink(jornadaGratuita)}
                  className="inline-flex items-center justify-center gap-2 text-sm font-medium text-[#2D6A4F] hover:underline"
                >
                  <Copy className="w-3.5 h-3.5" />
                  Copiar meu link
                </button>
              </div>
            ) : (
              <p className="text-sm text-[#5F5E5A]">
                {gerandoGratuita ? 'Preparando seu acesso gratuito...' : 'Atualize a página se o acesso não aparecer em instantes.'}
              </p>
            )}
          </div>

          <div className="bg-white border border-[#EDE0D4] rounded-2xl p-6 mb-6">
            <h2 className="text-xl font-cormorant font-semibold text-[#2D6A4F] mb-2 flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              Seus créditos da Jornada
            </h2>
            <p className="text-sm text-[#5F5E5A] leading-relaxed mb-4">
              Você pode vender a Jornada Completa por R$97 para suas clientes, ou comprar a vivência por um valor promocional de R$30,00 para presenteá-las. Você pode comprar quantas pessoas desejar: a cada compra, você recebe 1 crédito e poderá gerar um link personalizado para sua cliente realizar a Jornada 12 Emoções.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <div className="bg-[#D8F3DC] rounded-xl px-5 py-3">
                <p className="text-xs uppercase tracking-wide text-[#2D6A4F] font-semibold">Saldo</p>
                <p className="text-2xl font-cormorant font-semibold text-[#2D6A4F]">
                  {carregandoCreditos ? '…' : creditos} {creditos === 1 ? 'crédito' : 'créditos'}
                </p>
              </div>
              <a
                href={LINK_JORNADA_CREDITO}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#2D6A4F] text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-opacity-90 transition-colors"
              >
                Comprar crédito — R$ 30,00
              </a>
            </div>
          </div>
        </>
      )}

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-cormorant font-semibold text-texto-escuro flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-verde-escuro" />
            Clientes nas Vivências
          </h2>
          <p className="text-sm text-texto-suave mt-1">
            {visaoAdmin
              ? 'Cadastre uma cliente para gerar o link de acesso à Jornada das 12 Emoções.'
              : 'Use 1 crédito para cadastrar uma cliente e gerar o link de acesso. Você vê apenas as clientes liberadas com os seus créditos.'}
          </p>
        </div>
        <button
          onClick={() => {
            if (!visaoAdmin && creditos < 1) {
              toast.error('Compre 1 crédito para cadastrar uma cliente.')
              return
            }
            setModalAberto(true)
          }}
          className="flex items-center gap-2 bg-verde-escuro text-white px-5 py-2.5 rounded-xl hover:bg-opacity-90 transition-all font-medium text-sm flex-shrink-0"
        >
          <Plus className="w-4 h-4" />
          Nova cliente
        </button>
      </div>

      {carregando ? (
        <div className="bg-white p-8 rounded-xl shadow-md text-center text-texto-suave">Carregando...</div>
      ) : clientes.length === 0 ? (
        <div className="bg-white p-10 rounded-xl shadow-md text-center">
          <Sparkles className="w-10 h-10 text-texto-suave mx-auto mb-3" />
          <p className="text-texto-suave mb-1">Nenhuma cliente cadastrada nas Vivências ainda.</p>
          <p className="text-sm text-texto-suave">Clique em "Nova cliente" para começar.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {clientes.map(cliente => {
            const link = `${BASE_URL}/vivencias/${cliente.token}`
            const cores = corStatus(cliente.status)
            return (
              <div key={cliente._id} className="bg-white rounded-xl shadow-md p-6">
                <div className="flex flex-col md:flex-row md:items-start gap-4 justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1 flex-wrap">
                      <h3 className="text-lg font-cormorant font-semibold text-texto-escuro flex items-center gap-2">
                        <UserIcon className="w-4 h-4 text-texto-suave" />
                        {cliente.nome}
                      </h3>
                      <span
                        className="px-2 py-0.5 rounded-full text-xs font-medium"
                        style={{ backgroundColor: cores.bg, color: cores.text }}
                      >
                        {labelStatus(cliente.status)}
                      </span>
                      <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                        {labelProgresso(cliente)}
                      </span>
                    </div>
                    <p className="text-sm text-texto-suave">
                      {cliente.email || ''}{cliente.whatsapp ? ` · ${cliente.whatsapp}` : ''}
                    </p>
                    {visaoAdmin ? (
                      <p className="text-xs text-texto-suave mt-0.5">Consultora: {cliente.consultora}</p>
                    ) : null}
                    <div className="mt-3 flex items-center gap-2 bg-bege-fundo border border-bege-medio rounded-xl px-3 py-2">
                      <span className="text-xs text-texto-suave truncate flex-1">{link}</span>
                      <button
                        onClick={() => copiarLink(cliente)}
                        className="flex items-center gap-1 text-xs text-verde-escuro hover:text-verde-claro transition-colors flex-shrink-0 font-medium"
                      >
                        <Copy className="w-3.5 h-3.5" />
                        Copiar link
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => setConfirmarDelete(cliente)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Remover cliente"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {modalAberto ? (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-bege-medio">
              <h2 className="text-xl font-cormorant font-semibold text-texto-escuro flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-verde-escuro" />
                Cadastrar nova cliente
              </h2>
              <button onClick={() => setModalAberto(false)} className="text-texto-suave hover:text-texto-escuro">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSalvar} className="p-6 space-y-4">
              <div>
                <label className="block text-sm text-texto-suave mb-1 font-medium">Nome da cliente *</label>
                <input
                  type="text"
                  value={nome}
                  onChange={e => setNome(e.target.value)}
                  placeholder="Ex: Ana Silva"
                  required
                  className="w-full px-4 py-2.5 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-texto-suave mb-1 font-medium">E-mail *</label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="cliente@email.com"
                  required
                  className="w-full px-4 py-2.5 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none"
                />
                <p className="text-xs text-texto-suave mt-1">
                  O e-mail é obrigatório para envio do link de acesso e dos relatórios das vivências.
                </p>
              </div>
              <div>
                <label className="block text-sm text-texto-suave mb-1 font-medium">WhatsApp (opcional)</label>
                <input
                  type="text"
                  value={whatsapp}
                  onChange={e => setWhatsapp(e.target.value)}
                  placeholder="+55 11 99999-9999"
                  className="w-full px-4 py-2.5 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none"
                />
              </div>
              <p className="text-xs text-texto-suave">
                {visaoAdmin
                  ? 'Ao cadastrar, um link exclusivo será gerado e a Vivência 1 já estará liberada para a cliente.'
                  : 'Ao cadastrar, 1 crédito será usado. Um link exclusivo será gerado e a Vivência 1 já estará liberada.'}
              </p>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setModalAberto(false)}
                  className="flex-1 px-4 py-3 border-2 border-bege-medio text-texto-escuro rounded-xl hover:bg-bege-fundo transition-colors font-medium">
                  Cancelar
                </button>
                <button type="submit" disabled={salvando}
                  className="flex-1 px-4 py-3 bg-verde-escuro text-white rounded-xl hover:bg-opacity-90 transition-colors font-medium disabled:opacity-60">
                  {salvando ? 'Salvando...' : 'Cadastrar cliente'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {confirmarDelete ? (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-cormorant font-semibold text-texto-escuro">
                Remover "{confirmarDelete.nome}"?
              </h3>
            </div>
            <p className="text-texto-suave text-sm mb-6 leading-relaxed">
              O link de acesso desta cliente deixará de funcionar. Essa ação é irreversível.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmarDelete(null)} disabled={deletando}
                className="flex-1 px-4 py-3 border-2 border-bege-medio text-texto-escuro rounded-xl hover:bg-bege-fundo transition-colors font-medium disabled:opacity-60">
                Cancelar
              </button>
              <button onClick={deletarCliente} disabled={deletando}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium flex items-center justify-center gap-2 disabled:opacity-60">
                <Trash2 className="w-4 h-4" />
                {deletando ? 'Removendo...' : 'Sim, remover'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}
